import { useEffect, useRef, useState } from "react";
import { MyNoiseGradadientBackground } from "../../components/backgrounds/MyNoiseGradadientBackground";
import { MyButton } from "../../components/MyButton";
import { Navbar } from "../../components/Navbar";
import "./Landing.scss";
import gsap from "gsap";
import { MyVideoBackground } from "../../components/backgrounds";
import Footer from "../../components/Footer";
import { IBackgroundTypeConfigSchema } from "../../config/interfaces";
import chroma from "chroma-js";
import { palette } from "../../config";
import { useWindowSize } from "@uidotdev/usehooks";
import { useLocation, useNavigate } from "react-router-dom";
import ProjectsCards from "./ProjectsCards";
import { SupportStepsSection } from "./SupportSteps";
import { FAQSection } from "./FAQSection";
import { ContactForm } from "./ContactFrom";
import { BREAKPOINTS } from "../../services/theme";
import { BannerSection } from "./BannerSection";
import { EcardSection } from "./EcardSection";
import { useProjectsData, usePreviewProjects } from "./projectsDATA";
import { logAnalytics, logCTA } from "../../services/firebase/analytics";
import SectionTracker from "../../services/firebase/analytics/SectionTracker";
import { useTranslation } from "react-i18next";
import { ScrollTrigger } from "gsap/ScrollTrigger";

export const LandingPage = () => {
  const { width: windowWith } = useWindowSize();
  const [navbarHeight, setNavbarHeight] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n, ready } = useTranslation();
  const projectsData = useProjectsData();
  const previewProjects = usePreviewProjects();

  // Debug: Log translation status
  console.log('LandingPage - i18n ready:', ready, 'language:', i18n.language);
  console.log('LandingPage - hero.claims:', t('landing:hero.claims'));
  console.log('LandingPage - projectsData length:', projectsData.length);
  console.log('LandingPage - previewProjects length:', previewProjects.length);
  console.log('LandingPage - test projects translation:', t('projects:items', { returnObjects: true }));

  // SECTIONS
  const statsSection = useRef<HTMLDivElement>(null);
  const projectsSection = useRef<HTMLDivElement>(null);
  const contactSection = useRef<HTMLDivElement>(null);
  const strengthSection = useRef<HTMLDivElement>(null);

  // SCROLL TO HASH
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
        navigate(`#`, { replace: true });
      }
    }
  }, [location.hash]);

  // ANIMAZIONI
  useEffect(() => {
    // Only keep the number counting animation for stats
    const tl2 = gsap.timeline({
      scrollTrigger: {
        trigger: ".stats__section",
        start: "top 60%",
        end: "top 30%",
        markers: false,
        toggleActions: "play none none reverse",
      },
    });

    // Animate the stat numbers from 0 to their final values
    tl2.fromTo(
      ".stat-1",
      { innerText: 0 },
      {
        innerText: "39.1%",
        duration: 2,
        ease: "power2.out",
        snap: { innerText: 1 },
      }
    );
    tl2.fromTo(
      ".stat-2",
      { innerText: 0 },
      {
        innerText: "42.9%",
        duration: 2,
        ease: "power2.out",
        snap: { innerText: 1 },
      },
      "-=1"
    );
    tl2.fromTo(
      ".stat-3",
      { innerText: 0 },
      {
        innerText: "41.4%",
        duration: 2,
        ease: "power2.out",
        snap: { innerText: 1 },
      },
      "-=1"
    );

    return () => {
      tl2.kill();
      // remove all triggers
      ScrollTrigger.getAll().forEach((trigger) => {
        trigger.kill();
      });
    };
  }, [i18n.language]); // Add i18n.language as dependency

  // REMOVED Intersection Observer for result-preview-card

  const goSection = (sectionRef: React.RefObject<HTMLElement>) => {
    if (sectionRef.current) {
      gsap.to(window, {
        duration: 0.5,
        scrollTo: sectionRef.current,
      });
    }
  };

  const onHeroCTAClick = () => {
    logCTA("hero_cta", "hero_section", { section_id: "about" });
    goSection(statsSection);
  };

  return (
    <div className="landing__page">
      <Navbar onHeightCalculated={setNavbarHeight} />

      <SectionTracker className="hero__section" id="hero" sectionName="hero">
        <div className="hero_background" />

        <MyVideoBackground
          backgroundConfig={[
            {
              breakpoint: "md",
              value: {
                background_type: IBackgroundTypeConfigSchema.VIDEO,
                background_src: "/assets/video/test6m.m4v",
                background_overlay: chroma(palette.backgroundDark)
                  .alpha(0.6)
                  .css(),
              },
            },
            {
              value: {
                background_type: IBackgroundTypeConfigSchema.VIDEO,
                background_src: "/assets/video/test6m.m4v",
                background_overlay_blur_radius: "20px",
                background_overlay: chroma(palette.backgroundDark)
                  .alpha(0.6)
                  .css(),
              },
            },
          ]}
        />

        <div
          className="hero-top"
          style={{
            paddingTop: navbarHeight,
            opacity: navbarHeight ? 1 : 0,
            transition: "opacity 0.5s",
          }}
        >
          <div className="hero-container mb-5">
            <div className="hero-title">
              <h2 className="hero-title__claims">
                {t('landing:hero.claims') || 'Consapevole. Sicura. Proprietaria.'}
              </h2>
              <br />
              <h1 
                className="hero-title__text"
                dangerouslySetInnerHTML={{ 
                  __html: t('landing:hero.title') || "Porta la tua azienda nel futuro con l'<span class=\"highlighted-span\">Intelligenza Artificiale</span> su misura"
                }}
              />
            </div>
          </div>

          <div className="cta d-flex justify-content-left mt-5 mb-2">
            <MyButton onClick={onHeroCTAClick} variant="light">
              <div
                className="p-1 d-flex align-items-center"
                style={{ color: palette.whiteSmoke }}
              >
                <h6
                  className="my-0"
                  style={{
                    marginRight: "10px",
                  }}
                >
                  <strong>{t('landing:hero.cta') || 'Scopri di più'}</strong>
                </h6>
                <i className="fas fa-arrow-right"></i>
              </div>
            </MyButton>
          </div>
        </div>
      </SectionTracker>

      {/**
       * -----------------------------------------
       * -----------------------------------------
       * STATS
       * -----------------------------------------
       * -----------------------------------------
       */}
      <SectionTracker sectionName="stats">
        <div className="stats__section section-container" ref={statsSection} id="stats">
          <MyNoiseGradadientBackground />
          <h1 
            style={{ paddingTop: "7rem" }} 
            className="section-title"
            dangerouslySetInnerHTML={{ __html: t('landing:stats.title') || "L'intelligenza artificiale <span>oggi</span> può" }}
          />
          <div className="container-fluid d-flex align-items-center justify-content-center text-center text-light">
            <div className="row mt-4 w-100">
              <div className="col-md-4">
                <div className="stat-card">
                  <h4>{t('landing:stats.card1.title') || 'Sostituire il'}</h4>
                  <h1 className="stat stat-1">0</h1>
                  <p>{t('landing:stats.card1.description') || 'delle attività ripetitive'}</p>
                </div>
              </div>
              <div className="col-md-4">
                <div className="stat-card">
                  <h4>{t('landing:stats.card2.title') || 'Ridurre del'}</h4>
                  <h1 className="stat stat-2">0</h1>
                  <p>{t('landing:stats.card2.description') || 'il carico di lavoro'}</p>
                </div>
              </div>
              <div className="col-md-4">
                <div className="stat-card">
                  <h4>{t('landing:stats.card3.title') || 'Aumentare del'}</h4>
                  <h1 className="stat stat-3">0</h1>
                  <p>{t('landing:stats.card3.description') || 'delle produttività'}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="results-preview">
            <h1 className="mb-2">{t('landing:stats.resultsTitle') || 'E lo abbiamo fatto...'}</h1>
            <div className="container-fluid d-flex align-items-center justify-content-center text-center text-light">
              <div className="row d-flex mt-4 w-100">
                {previewProjects.map((item) => (
                  <div
                    className="col-md-4 col-10 p-lg-3 p-3 p-md-1"
                    key={item.title}
                    style={{
                      maxWidth: "400px",
                      overflowY: "hidden",
                    }}
                  >
                    <div className="result-preview-card" style={{ opacity: 1 }}>
                      <img 
                        src={item.image} 
                        alt={item.title} 
                        className="w-100"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="overlay-content">
                        <div className="overlay-content__text">
                          <h4>
                            {windowWith &&
                            windowWith < 800 &&
                            windowWith > BREAKPOINTS.md
                              ? item.title.slice(0, 40) + "..."
                              : item.title.split(" ").slice(0, 60).join(" ") +
                                (item.title.split(" ").length > 60 ? "..." : "")}

                            <br />
                          </h4>
                        </div>
                        <div
                          className="overlay-content__btn"
                          onClick={() => goSection(projectsSection)}
                        >
                          <div className="btn btn-transparent mt-4 text-light">
                            {t('landing:stats.learnMoreBtn') || 'Scopri di più'}
                            <i
                              className="fas fa-arrow-right"
                              style={{ fontSize: "1rem", marginLeft: ".5rem" }}
                            ></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div
            className="section__cta mt-5 border-none "
            onClick={() => {
              goSection(projectsSection);
              logCTA("show_more_projects", "stats_section", {
                section_id: "projects",
              });
            }}
          >
            {t('landing:stats.moreProjectsBtn') || 'Vedi altri progetti'}
            <i
              className="fas fa-chevron-right"
              style={{
                fontSize: "1.5rem",
                marginLeft: "1rem",
              }}
            ></i>
          </div>
        </div>
      </SectionTracker>

      {/**
       * -----------------------------------------
       * -----------------------------------------
       * ABOUT
       * -----------------------------------------
       * -----------------------------------------
       */}
      <SectionTracker
        className="about__section pt-5 section-container"
        sectionName="about"
        id="about"
      >
        <div className="about-content">
          <img
            src="/assets/logo-mini.png"
            alt="EffAI"
            className="about__logo mt-5"
          />

          <h1 
            className="section-title mt-4 mb-5"
            dangerouslySetInnerHTML={{ __html: t('landing:about.title') }}
          />

          <div
            style={{ width: "100%", display: "flex", justifyContent: "center" }}
          >
            <p 
              className="text-block"
              dangerouslySetInnerHTML={{ __html: t('landing:about.description') }}
            />
          </div>

          <div
            className="section__cta mt-5"
            onClick={() => {
              goSection(strengthSection);
              logCTA("about_cta", "about_section", {
                section_id: "our-strength",
              });
            }}
          >
            <div dangerouslySetInnerHTML={{ 
              __html: t('landing:about.cta') || 'Scopri come <span class="highlighted-span mx-1">EffAI</span> può rivoluzionare la tua azienda'
            }} />
            <i
              className="fas fa-chevron-right"
              style={{ fontSize: "1.5rem", marginLeft: "1rem" }}
            ></i>
          </div>
        </div>
      </SectionTracker>

      {/**
       * -----------------------------------------
       * -----------------------------------------
       * LE NOSTRE FORZE
       * -----------------------------------------
       * -----------------------------------------
       */}
      <SectionTracker sectionName="our-strength">
        <div
          className="strength__section section-container"
          ref={strengthSection}
          id="our-strength"
        >
          <div
            style={{
              paddingTop: "7rem",
            }}
          >
            <h1 className="section-title" style={{}}>
              {t('landing:strength.title') || 'Cosa possiamo fare per te?'}
            </h1>
          </div>

          <div className="py-5 strength-content">
            <div className="feature-row row align-items-center ">
              <div className="feature-row-bg"></div>

              <h5 
                className="row-pre-title d-block d-md-none"
                dangerouslySetInnerHTML={{ __html: t('landing:strength.section1.preTitle') }}
              />

              <div className="col-md-6 icon-block text-center">
                <img
                  src="/assets/gifs/mentorship.gif"
                  alt="mentorship"
                  className="w-50"
                />
              </div>
              <div className="col-md-6  content-block">
                <h5 
                  className="row-pre-title d-none d-md-block"
                  dangerouslySetInnerHTML={{ __html: t('landing:strength.section1.preTitle') }}
                />

                <h5 className="row-title">
                  {t('landing:strength.section1.title') || 'Formazione privata per team e management'}
                </h5>
                <p className="row-text">
                  {t('landing:strength.section1.description') || 'Strumenti intelligenti per formare squadre vincenti, migliorare la collaborazione e ottimizzare il management aziendale.'}
                </p>

                <div className="cta  mt-5 w-100 w-md-auto">
                  <MyButton
                    onClick={() => {
                      goSection(contactSection);
                      logCTA(
                        "strength_cta[Un approccio pratico]",
                        "our-strength",
                        {
                          section_id: "contact",
                        }
                      );
                    }}
                    variant="light"
                    className="w-100 w-md-auto"
                  >
                    <div
                      className="p-1 d-flex align-items-center"
                      style={{ color: palette.whiteSmoke }}
                    >
                      <h6
                        className="my-0 "
                        style={{
                          marginRight: "10px",
                        }}
                      >
                        <strong>{t('landing:strength.section1.cta') || 'Scopri di più'}</strong>
                      </h6>
                      <i className="fas fa-arrow-right"></i>
                    </div>
                  </MyButton>
                </div>
              </div>
            </div>

            <div className="feature-row row align-items-center right">
              <div className="feature-row-bg"></div>

              <h5 
                className="row-pre-title d-block d-md-none"
                dangerouslySetInnerHTML={{ __html: t('landing:strength.section2.preTitle') }}
              />

              <div className="col-md-6 order-md-2 icon-block">
                <img
                  src="/assets/gifs/conversation.gif"
                  alt="conversation"
                  className="w-50"
                />
              </div>
              <div className="col-md-6 order-md-1 content-block">
                <h5 
                  className="row-pre-title d-none d-md-block"
                  dangerouslySetInnerHTML={{ __html: t('landing:strength.section2.preTitle') }}
                />
                <h5 className="row-title">{t('landing:strength.section2.title') || 'Consulenza e studi di fattibilità'}</h5>

                <p className="row-text">
                  {t('landing:strength.section2.description') || "L'IA è anche per le piccole imprese! Studiamo insieme la sostenibilità, le risorse e la fattibilità per l'integrazione dell'IA nella tua azienda"}
                </p>

                <div className="cta  mt-5 w-100 w-md-auto">
                  <MyButton
                    onClick={() => {
                      goSection(contactSection);
                      logCTA(
                        "strength_cta[Capiamo insieme come farlo]",
                        "our-strength",
                        {
                          section_id: "contact",
                        }
                      );
                    }}
                    variant="light"
                    className="w-100 w-md-auto"
                  >
                    <div
                      className="p-1 d-flex align-items-center"
                      style={{ color: palette.whiteSmoke }}
                    >
                      <h6
                        className="my-0"
                        style={{
                          marginRight: "10px",
                        }}
                      >
                        <strong>{t('landing:strength.section2.cta') || 'Come possiamo aiutarti?'}</strong>
                      </h6>
                      <i className="fas fa-arrow-right"></i>
                    </div>
                  </MyButton>
                </div>
              </div>
            </div>

            <div className="feature-row row align-items-center ">
              <div className="feature-row-bg"></div>

              <h5 
                className="row-pre-title d-block d-md-none"
                dangerouslySetInnerHTML={{ __html: t('landing:strength.section3.preTitle') }}
              />

              <div className="col-md-6 icon-block">
                <img
                  src="/assets/gifs/user.gif"
                  alt="for you"
                  className="w-50"
                />
              </div>

              <div className="col-md-6 content-block">
                <h5 
                  className="row-pre-title d-none d-md-block"
                  dangerouslySetInnerHTML={{ __html: t('landing:strength.section3.preTitle') }}
                />
                <h5 className="row-title">{t('landing:strength.section3.title') || 'Servizi su misura per te'}</h5>
                <p className="row-text">
                  {t('landing:strength.section3.description') || 'Ogni progetto viene pensato insieme per garantire un ritorno concreto nelle attivitá inefficienti della tua azienda.'}
                </p>
                <div className="cta  mt-5 w-100 w-md-auto">
                  <MyButton
                    onClick={() => {
                      goSection(contactSection);
                      logCTA(
                        "our_strength_cta[Personalizzato per te]",
                        "our_strength_section",
                        {
                          section_id: "contact",
                        }
                      );
                    }}
                    variant="light"
                    className="w-100 w-md-auto"
                  >
                    <div
                      className="p-1 d-flex align-items-center"
                      style={{ color: palette.whiteSmoke }}
                    >
                      <h6
                        className="my-0"
                        style={{
                          marginRight: "10px",
                        }}
                      >
                        <strong>{t('landing:strength.section3.cta') || 'Iniziamo!'}</strong>
                      </h6>
                      <i className="fas fa-arrow-right"></i>
                    </div>
                  </MyButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SectionTracker>

      {/**
       * -----------------------------------------
       * -----------------------------------------
       * PRIVACY
       * -----------------------------------------
       * -----------------------------------------
       */}
      <SectionTracker className="privacy__section" sectionName="privacy">
        {/* <MyNoiseGradadientBackground
          backgroundColor={palette.backgroundDark}
          rotate
        /> */}

        <div className="privacy-content">
          <div className="wrapper">
            <img
              src="/assets/gifs/shield.gif"
              alt="privacy"
              style={{
                height: 200,
                margin: "0 auto",
                width: "100%",
                objectFit: "contain",
              }}
            />

            <div className="privacy__section__title">
              <h1>
                <strong>{t('landing:privacy.title1') || 'Privacy'}</strong>
              </h1>
              <h2>
                <strong>{t('landing:privacy.title2') || 'Sicurezza'}</strong>
              </h2>
            </div>

            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <p className="text-block">
                {t('landing:privacy.description1') || 'Implementiamo le più avanzate tecnologie di sicurezza per garantire la riservatezza e l\'integrità delle informazioni, rispettando rigorosamente normative come GDPR e AI Act. Il nostro approccio si basa su crittografia avanzata, gestione trasparente dei consensi e soluzioni su misura per la protezione della privacy, assicurando ai nostri clienti un\'esperienza sicura e affidabile.'}
              </p>
            </div>
          </div>

          <div className="wrapper">
            <img
              src="/assets/gifs/dollar.gif"
              alt="privacy"
              style={{
                height: 200,
                margin: "0 auto",
                width: "100%",
                objectFit: "contain",
              }}
            />

            <div className="privacy__section__title">
              <h2>
                <strong>{t('landing:privacy.bandiTitle1') || 'Sostegno'}</strong>
              </h2>
              <h1>
                <strong>{t('landing:privacy.bandiTitle2') || 'Bandi'}</strong>
              </h1>
            </div>

            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <p className="text-block">
                {t('landing:privacy.bandiDescription') || 'Ogni progetto viene pensato insieme per garantire un ritorno concreto nelle attivitá inefficienti della tua azienda. I nostri team ti guidano nella raccolta fondi ed allinearti con le normative.'}
              </p>
            </div>
          </div>
        </div>
      </SectionTracker>

      {/**
       * -----------------------------------------
       * -----------------------------------------
       * SUPPORT STEPS
       * -----------------------------------------
       * -----------------------------------------
       */}
      <SectionTracker sectionName="support-steps">
        <SupportStepsSection paddingTop={navbarHeight || 0} />
      </SectionTracker>

      {/**
       * -----------------------------------------
       * -----------------------------------------
       * PROJECTS
       * -----------------------------------------
       * -----------------------------------------
       */}
      <SectionTracker sectionName="projects">
        <div className="projects__section section-container" ref={projectsSection} id="projects">
          <div className="projects-content">
            <h1 className="section-title" style={{ paddingTop: "7rem" }}>
              {t('landing:projects.title') || 'Casi studio'}
            </h1>

            <h2 
              className="projects__section__subtitle"
              dangerouslySetInnerHTML={{ 
                __html: t('landing:projects.subtitle') || 'Dove abbiamo già fatto la <span class="highlighted-span" style="font-style: italic;">differenza</span>?'
              }}
            />

            <ProjectsCards />
          </div>
        </div>
      </SectionTracker>

      {/**
       * -----------------------------------------
       * -----------------------------------------
       * BANNER
       * -----------------------------------------
       * -----------------------------------------
       */}
      <SectionTracker sectionName="banner">
        <BannerSection
          onClick={() => {
            goSection(contactSection);
            logCTA("banner_cta", "banner_section", { section_id: "contact" });
          }}
        />
      </SectionTracker>

      {/**
       * -----------------------------------------
       * ECARD — Free gift (before FAQ)
       * -----------------------------------------
       */}
      <SectionTracker sectionName="ecard">
        <EcardSection />
      </SectionTracker>

      {/**
       * -----------------------------------------
       * -----------------------------------------
       * FAQ
       * -----------------------------------------
       * -----------------------------------------
       */}
      <FAQSection />

      {/**
       * -----------------------------------------
       * -----------------------------------------
       * CONTACTS
       * -----------------------------------------
       * -----------------------------------------
       */}
      <SectionTracker
        sectionName="contact"
        onEnter={() => {
          // Log the user entered the contact section
          logAnalytics("entered_contact_section");
        }}
      >
        <ContactForm sectionRef={contactSection} />
      </SectionTracker>

      <Footer />
    </div>
  );
};
